// Core packages
import { CollectionViewer } from "@angular/cdk/collections";
import { DataSource } from "@angular/cdk/table";

// Third party packages
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, finalize } from "rxjs/operators";

// Custom packages
import { SuppliersService } from "src/app/modules/suppliers/suppliers.service";
import { Supplier } from "src/app/shared/models/supplier/supplier.model";
import { HelperService } from "src/app/shared/services/helper.service";
import { IUser } from "../../models/user/user.interface";

/**
 * @see https://blog.angular-university.io/angular-material-data-table/
 *
 * NB
 * If you need to change this to make a new DataSource
 * just change the following:
 *
 * - Change The class name in the first line
 * - Change DataSource<T> (use your model type) in first line
 * - Change BehaviourSubject<T> in the second line
 * - Change the service inside the class constructor
 * - Change the Observable<T> used in the connect() line
 * - Change the service name inside loadItems()
 */
export class SuppliersNewDataSource implements DataSource<Supplier> {
  private itemsSubject$ = new BehaviorSubject<Supplier[]>([]);
  private totalCountSubject$ = new BehaviorSubject<number>(0);
  private totalActiveCountSubject$ = new BehaviorSubject<number>(0);
  private loadingSubject$ = new BehaviorSubject<boolean>(true);

  public loading$ = this.loadingSubject$.asObservable();
  public totalCount$ = this.totalCountSubject$.asObservable();
  public totalActiveCount$ = this.totalActiveCountSubject$.asObservable();

  /**
   * Class constructor
   */
  constructor(
    private suppliersService: SuppliersService,
    private helperService: HelperService,
    private onlyNewInTank?: boolean,
    private translateService?: TranslateService
  ) {}

  /**
   * Connect data table to itemsSubject$
   *
   * @since 1.0.0
   *
   * @param collectionViewer collection viewer object
   * @returns Observable
   */
  connect(collectionViewer: CollectionViewer): Observable<Supplier[]> {
    return this.itemsSubject$.asObservable();
  }

  /**
   * Disconnect data table from subjects
   *
   * @since 1.0.0
   *
   * @param collectionViewer collection viewer object
   */
  disconnect(collectionViewer: CollectionViewer): void {
    this.itemsSubject$.complete();
    this.totalCountSubject$.complete();
    this.totalActiveCountSubject$.complete();
    this.loadingSubject$.complete();
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   * @param [master] Only users associated to master company
   */
  loadItems(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any,
    master?: boolean,
    allMasters?: boolean
  ): void {
    this.loadingSubject$.next(true); // Start loading
    if (!search || !search.types || !search.types.includes("supplier")) {
      search.types = ["supplier"];
    }
    if (typeof this.onlyNewInTank !== "undefined" && this.onlyNewInTank) {
      this.suppliersService
        .getNewSuppliersInTank(start, limit, sort, sortVersus, search)
        .pipe(
          catchError((err) => {
            // If errors, return empty array
            this.helperService.handleError(err);
            return of({ status: true, data: [], totalCount: 0 });
          }),
          finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
        )
        .subscribe((result: any) => {
          this.itemsSubject$.next(result.data);
          this.totalCountSubject$.next(result.totalCount);
          if (typeof result.totalCount !== "undefined") {
            this.totalActiveCountSubject$.next(result.totalCount);
          }
        });
    } else {
      this.suppliersService
        .getListV2(start, limit, sort, sortVersus, search, master, allMasters)
        .pipe(
          catchError((err) => {
            // If errors, return empty array
            this.helperService.handleError(err);
            return of({ status: true, data: [], totalCount: 0 });
          }),
          finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
        )
        .subscribe((result: any) => {
          if (result.data && Array.isArray(result.data)) {
            result.data.forEach((item: IUser) => {
              if (item.ownDocuments && Array.isArray(item.ownDocuments)) {
                for (const doc of item.ownDocuments) {
                  if (doc.type === "durc" || doc.type === "exemptionDurc") {
                    item.durc = doc;
                  }
                  if (doc.type === "registration") {
                    item.registration = doc;
                  }
                  if (doc.type === "exemptionPac") {
                    item.creditLicence = doc;
                  }
                  if (doc.type === "pac" || doc.type === "soa") {
                    item.creditLicence = doc;
                    item.credits = doc.credits;
                  }
                }
              }
              if (item.categories && Array.isArray(item.categories)) {
                let categories = "";
                for (const category of item.categories) {
                  const catTranslated = this.translateService.instant(
                    `suppliers.categories.${category}`
                  );
                  categories = categories !== "" ? `${categories},` : "";
                  categories = `${categories} ${catTranslated}`;
                }
                item.categories.length = 0;
                item.categories.push(categories);
              }
              if (
                item.legalAddress &&
                (item.legalAddress.city ||
                  item.legalAddress.administrativeArea2)
              ) {
                item.municipalityProvince = item.legalAddress.city
                  ? item.legalAddress.city
                  : "";
                item.municipalityProvince = `${item.municipalityProvince} ${
                  item.legalAddress.administrativeArea2
                    ? item.legalAddress.administrativeArea2
                    : ""
                }`;
              }
            });
          }
          this.itemsSubject$.next(result.data);
          this.totalCountSubject$.next(result.totalCount);
          if (typeof result.totalCount !== "undefined") {
            this.totalActiveCountSubject$.next(result.totalCount);
          }
        });
    }
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   * @param [master] Only users associated to master company
   */
  loadOnlyNfcItems(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): void {
    this.loadingSubject$.next(true); // Start loading
    this.suppliersService
      .getListVOnlyNfc(start, limit, sort, sortVersus, search)
      .pipe(
        catchError((err) => {
          // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: [], totalCount: 0 });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data);
        this.totalCountSubject$.next(result.totalCount);
      });
  }
}
